import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { paymentService, SubscriptionPackage } from '../services/payment';
import { ArrowBack } from '@mui/icons-material';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isTr = language === 'tr';
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async (packageType: string) => {
    setProcessing(packageType);
    setError(null);
    try {
      const successUrl = `${window.location.origin}/payment/success?package=${packageType}`;
      const cancelUrl = `${window.location.origin}/payment/cancel`;
      const checkout = await paymentService.createCheckout({
        package_type: packageType,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      if (checkout.checkout_url) {
        window.location.href = checkout.checkout_url;
      } else {
        throw new Error('Checkout URL alınamadı');
      }
    } catch (err: any) {
      console.error('Ödeme hatası:', err);
      setError(err?.response?.data?.detail || err?.message || (isTr ? 'Ödeme başlatılamadı' : 'Failed to start checkout'));
      setProcessing(null);
    }
  };

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const data = await paymentService.getPackages();
        setPackages(data.packages);
        const packageParam = searchParams.get('package');
        if (packageParam && data.packages.some(p => p.package_type === packageParam)) {
          setTimeout(() => handlePurchase(packageParam), 500);
        }
      } catch (err: any) {
        setError(err?.response?.data?.detail || (isTr ? 'Paketler yüklenemedi' : 'Failed to load packages'));
      } finally {
        setLoading(false);
      }
    };
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    if (success === 'true') {
      setTimeout(() => navigate('/dashboard'), 3000);
    } else if (cancelled === 'true') {
      setError(isTr ? 'Ödeme iptal edildi' : 'Payment cancelled');
    }
  }, [searchParams, navigate, isTr]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
          <div className="h-8 w-48 mb-12 bg-elevated animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-hairline border border-hairline">
            {[0, 1, 2].map(i => <div key={i} className="bg-background h-80 animate-pulse" />)}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <button
          onClick={() => navigate(-1)}
          className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-mute hover:text-accent transition-colors flex items-center gap-2 mb-10"
        >
          <ArrowBack sx={{ fontSize: 14 }} />
          {isTr ? 'GERİ' : 'BACK'}
        </button>

        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'ABONELİK' : 'SUBSCRIPTION'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'GÜVENLİ ÖDEME · PADDLE' : 'SECURE CHECKOUT · PADDLE'}</span>
        </div>

        <div className="max-w-3xl mb-16">
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Aylık <em>token akışı.</em></> : <>Monthly <em>token flow.</em></>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'Plan seç, aylık tokenlar otomatik yenilensin. Kullanmadığın bir sonraki aya devreder. İstediğin zaman iptal edebilirsin.'
              : 'Pick a plan and let monthly tokens renew automatically. Unused tokens carry over. Cancel anytime.'}
          </p>
        </div>

        {error && (
          <div className="mb-10 p-5 border border-signal-red/40 bg-signal-red/10">
            <span className="eyebrow-mute mb-1 block text-signal-red">/ ERROR</span>
            <p className="font-prose text-[14px] text-signal-red leading-relaxed">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16 max-w-5xl mx-auto">
          {packages.map((pkg, i) => {
            const isPopular = pkg.monthly_tokens === 5;
            const pricePerToken = (pkg.price / pkg.monthly_tokens).toFixed(2);
            return (
              <div
                key={pkg.package_type}
                className={`relative border p-8 transition-colors ${
                  isPopular
                    ? 'border-accent bg-accent-soft'
                    : 'border-hairline bg-surface hover:border-accent/60'
                }`}
              >
                <div className="flex items-baseline justify-between mb-6">
                  <span className="eyebrow text-ink-mute">/{String(i + 1).padStart(2, '0')}</span>
                  {isPopular && (
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-accent">
                      ★ {isTr ? 'EN POPÜLER' : 'POPULAR'}
                    </span>
                  )}
                </div>

                <div className="mb-8">
                  <span className="font-mono text-[11px] tracking-wider uppercase text-ink-mute mb-2 block">
                    {pkg.name}
                  </span>
                  <div className="font-serif italic text-5xl text-ink leading-none mb-2">
                    {pkg.monthly_tokens}
                    <span className="font-mono not-italic text-[14px] text-ink-mute ml-2 tracking-wider align-top">
                      {isTr ? 'TOKEN' : 'TOKENS'}/{isTr ? 'AY' : 'MO'}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-3xl font-bold text-ink tabular-nums">${pkg.price.toFixed(2)}</span>
                    <span className="font-mono text-[11px] tracking-wider uppercase text-ink-mute">
                      · ${pricePerToken}/{isTr ? 'token' : 'token'}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-10 font-prose text-[14px] text-ink-dim leading-relaxed">
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{pkg.monthly_tokens} {isTr ? 'aylık token' : 'monthly tokens'}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{isTr ? 'Otomatik yenileme' : 'Automatic renewal'}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{isTr ? 'İstediğin zaman iptal' : 'Cancel anytime'}</span>
                  </li>
                  {pkg.description && (
                    <li className="flex gap-3">
                      <span className="text-accent font-mono mt-0.5">→</span>
                      <span>{pkg.description}</span>
                    </li>
                  )}
                </ul>

                <button
                  onClick={() => handlePurchase(pkg.package_type)}
                  disabled={processing === pkg.package_type || !!processing}
                  className={(isPopular ? 'btn-accent' : 'btn-ghost') + ' w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed'}
                >
                  {processing === pkg.package_type ? (
                    <>
                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                      {isTr ? 'İŞLENİYOR…' : 'PROCESSING…'}
                    </>
                  ) : (
                    <>
                      {isTr ? 'ABONE OL' : 'SUBSCRIBE'}
                      <span aria-hidden>→</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust band */}
        <div className="border border-hairline p-8 md:p-10 max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between mb-4">
            <span className="eyebrow"><span className="text-accent">/02</span> · {isTr ? 'GÜVENLİK' : 'SECURITY'}</span>
            <span className="eyebrow-mute">SSL · PCI · 24/7</span>
          </div>
          <h3 className="display-h text-2xl mb-3">
            {isTr ? <>Güvenli, <em>şifreli, sade.</em></> : <>Secure, <em>encrypted, simple.</em></>}
          </h3>
          <p className="font-prose text-[15px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'Ödemelerin Paddle üzerinden işlenir. Kart bilgilerin bizde saklanmaz; tüm trafik SSL ile şifrelenir.'
              : 'Payments are processed through Paddle. Your card details are not stored with us; all traffic is SSL-encrypted.'}
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
